import React, { useState, useMemo, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';

// CONTEXT
import { AuthContext } from 'context/AuthContext';
import { ToastContext } from 'context/ToastContext';
import { useMenu } from 'context/MenuContext';

// HOOKS
import useUserData from 'hooks/useUserData';

// SERVICES
import { getTileProposals, declineTileProposal, deleteTileTypeLink } from 'services/tileService';

// COMPONENTS
import TableComponent from 'components/common/tableComponent';
import SearchBar from 'components/administration/searchBarComponent'
import Loader from 'components/loaderComponent';

// ICONS
import { Plus, Check, X } from 'lucide-react';

const ManagementProposalsPage = () => {
  const { addToast } = useContext(ToastContext);
  const { email, isInternal, isExternal, isAdmin, loading: authLoading } = useContext(AuthContext);
  const { setPendingProposals } = useMenu();
  const { userBDDInfo, loading: userLoading } = useUserData(email, isInternal, isExternal, isAdmin);
  const [userId, setUserId] = useState(null);
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [initialTilesData, setInitialTilesData] = useState([]);
  const [confirmingDeleteTileId, setConfirmingDeleteTileId] = useState(null);
  const colors = ['EF9700', '003B5E', '00BBBA', 'DB0661'];

  useEffect(() => {
    if (userBDDInfo?.data?.[0]?.USR_ID && !userLoading) {
      setUserId(userBDDInfo.data[0].USR_ID);
    }
  }, [userBDDInfo, userLoading]);

  const fetchProposals = async () => {
    try {
      const response = await getTileProposals();
      const data = response.data;
      const transformedData = data.map(proposal => ({
        id: String(proposal.PRP_ID),
        tileId: String(proposal.TIL_ID),
        status: {
          text: proposal.PRP_VALIDATED_AT ? 'Validée' : proposal.PRP_DECLINED_AT ? 'Refusée' : 'En attente',
          variant: proposal.PRP_VALIDATED_AT ? 'success' : proposal.PRP_DECLINED_AT ? 'error' : 'warning',
          tooltip: proposal.PRP_VALIDATED_AT 
            ? `Par : ${proposal.PRP_VALIDATED_BY}\nLe : ${new Date(proposal.PRP_VALIDATED_AT).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: 'UTC' })}`
            : proposal.PRP_DECLINED_AT 
              ? `Par : ${proposal.PRP_DECLINED_BY}\nLe : ${new Date(proposal.PRP_DECLINED_AT).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: 'UTC' })}`
              : null
        },
        title: proposal.PRP_TITLE,
        description: proposal.PRP_DESCRIPTION,
        url: proposal.PRP_LINK,
        availableForPresta: {
          text: proposal.PRP_AVAILABLE_FOR_PRESTA ? 'Oui' : 'Non',
          variant: proposal.PRP_AVAILABLE_FOR_PRESTA ? 'success' : 'neutral'
        },
        requiresVPN: {
          text: proposal.PRP_REQUIRES_VPN ? 'Oui' : 'Non',
          variant: proposal.PRP_REQUIRES_VPN ? 'success' : 'neutral'
        },
        reason: proposal.PRP_REASON,
        createdBy: {
          src: `https://bes-sso.altitudeinfra.fr/avatar/${proposal.PRP_CREATED_BY}`,
          alt: `Avatar de ${proposal.PRP_CREATED_BY}`,
          username: proposal.PRP_CREATED_BY
        },
        createdAt: proposal.PRP_CREATED_AT.replace('Z', ''),
      }));
      setInitialTilesData(transformedData);
      
      // Mettre à jour le nombre de propositions en attente
      const pendingCount = data.filter(proposal => !proposal.PRP_VALIDATED_AT && !proposal.PRP_DECLINED_AT).length;
      setPendingProposals(pendingCount);
    } catch (error) {
      console.error("Erreur lors de la récupération des propositions:", error);
      setInitialTilesData([]);
      setPendingProposals(0);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchProposals();
  }, []);

  const handleEdit = (item) => {
    if (!userId) {
      addToast('Erreur', 'Impossible de récupérer les informations utilisateur. Veuillez réessayer.', null, null, 'error');
      return;
    }
    
    // Sauvegarder les données de la proposition dans le localStorage
    localStorage.setItem('tileFormData', JSON.stringify({
      titre: item.title,
      description: item.description,
      lien: item.url,
      facultatif: true,
      availableForPresta: item.availableForPresta.text === 'Oui' ? true : false,
      requiresVPN: item.requiresVPN.text === 'Oui' ? true : false
    }));
    // Rediriger vers le formulaire avec l'état de navigation
    navigate('/admin/tiles/management/form', { 
      state: { 
        from: '/admin/tiles/proposals',
        proposalId: item.id,
        userId: userId
      }
    });
  };

  const handleRefuse = (item) => {
    setConfirmingDeleteTileId(item.id);
  };

  const executeConfirmedRefuse = async (item) => {
    if (!userId) {
        addToast('Erreur', 'Impossible de récupérer les informations utilisateur. Veuillez réessayer.', null, null, 'error');
        return;
    }
    try {
      // Valider la proposition avec un statut de refus
      await declineTileProposal(item.id, userId);
      
      // Si la proposition était validée, supprimer la tuile correspondante
      if (item.status.text === 'Validée') {
        try {
            await deleteTileTypeLink(item.tileId, userId);
        } catch (error) {
            console.error("Erreur lors de la suppression de la tuile:", error);
            addToast('Attention', 'La proposition a été refusée mais la tuile n\'a pas pu être supprimée.', null, null, 'warning');
        }
      }
      
      // Rafraîchir la liste des propositions
      await fetchProposals();
      setConfirmingDeleteTileId(null);
      
      // Afficher un message de succès
      addToast('Succès', 'La proposition a été refusée avec succès.', null, null, 'success');
    } catch (error) {
      console.error("Erreur lors du refus de la proposition:", error);
      addToast('Erreur', 'Une erreur est survenue lors du refus de la proposition.', null, null, 'error');
    }
  };

  const cancelRefuse = () => {
    setConfirmingDeleteTileId(null);
  };

  const tilesDataWithActions = useMemo(() => initialTilesData.map(tile => {
    const isConfirming = confirmingDeleteTileId === tile.id;
    let actions = [];

    if (isConfirming) {
      actions = [
        { icon: Check, onClick: () => executeConfirmedRefuse(tile), variant: 'success', label: 'Confirmer le refus' },
        { icon: X, onClick: cancelRefuse, variant: 'error', label: 'Annuler le refus' }
      ];
    } else {
      actions = [
        ...(tile.status.text !== 'Validée' ? [{ icon: Plus, onClick: () => handleEdit(tile), variant: 'success', label: 'Ajouter' }] : []),
        ...(tile.status.text !== 'Refusée' ? [{ icon: X, onClick: () => handleRefuse(tile), variant: 'error', label: 'Refuser' }] : [])
      ];
    }
    return {
      ...tile,
      actions
    };
  }), [initialTilesData, confirmingDeleteTileId, userId]);

  const filteredTiles = useMemo(() => {
    if (!searchTerm) {
      return tilesDataWithActions;
    }
    const lowerSearchTerm = searchTerm.toLowerCase();
    return tilesDataWithActions.filter(
      (tile) =>
        tile.title.toLowerCase().includes(lowerSearchTerm) ||
        (tile.description && tile.description.toLowerCase().includes(lowerSearchTerm)) ||
        (tile.url && tile.url.toLowerCase().includes(lowerSearchTerm))
    );
  }, [searchTerm, tilesDataWithActions]);

  const columns = useMemo(() => [
    { key: 'status', label: 'Statut', type: 'badge' },
    { key: 'title', label: 'Titre', type: 'text' },
    { key: 'description', label: 'Description', type: 'text' },
    { key: 'url', label: 'Lien', type: 'link' },
    { key: 'availableForPresta', label: 'Disponible pour prestataire', type: 'badge' },
    { key: 'requiresVPN', label: 'Nécessite VPN', type: 'badge' },
    { key: 'reason', label: 'Raison', type: 'textarea' },
    { key: 'createdBy', label: 'Créé par', type: 'profil' },
    { key: 'createdAt', label: 'Créé le', type: 'datetime' },
    { key: 'actions', label: 'Actions', type: 'action' },
  ], []);

  
  if (isLoading || authLoading || userLoading || !userId) {
    return <Loader />;
  }

  return (
    <div className="h-full flex flex-col">
      <div className="mb-6 flex flex-col sm:flex-row justify-between items-center">
        <SearchBar 
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      <div className="flex-1 min-h-0">
        <TableComponent columns={columns} rows={filteredTiles} emptyText="Aucune proposition trouvée." />
      </div>
    </div>
  );
};

export default ManagementProposalsPage;